import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  fetchSignInMethodsForEmail,
  getAuth,
} from 'firebase/auth';
import { deleteApp, initializeApp } from 'firebase/app';
import { auth } from '@/lib/firebase';
import { debugError } from '@/lib/debug';

const secondaryFirebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
};

/**
 * Authentication Service - Handles all Firebase Auth operations
 */
export class AuthService {
  /**
   * Sign in user with email and password
   * @param email User email
   * @param password User password
   * @param rememberMe Whether to use local persistence (true) or session persistence (false)
   */
  static async signIn(email: string, password: string, rememberMe: boolean = false) {
    // Set persistence based on "remember me" checkbox
    const persistence = rememberMe ? browserLocalPersistence : browserSessionPersistence;
    await setPersistence(auth, persistence);
    
    return await signInWithEmailAndPassword(auth, email, password);
  }

  /**
   * Create new user account
   * @param email User's email
   * @param password User's password
   */
  static async signUp(email: string, password: string) {
    return await createUserWithEmailAndPassword(auth, email, password);
  }

  /**
   * Create a new auth user without changing the currently signed-in user.
   * Useful for admin-side client creation.
   * @param email User's email
   * @param password User's password
   * @param displayName Display name to set on the created user
   */
  static async createUserWithoutSwitchingSession(
    email: string,
    password: string,
    displayName?: string
  ) {
    const secondaryApp = initializeApp(
      secondaryFirebaseConfig,
      `client-signup-${Date.now()}`
    );
    const secondaryAuth = getAuth(secondaryApp);

    try {
      const userCredential = await createUserWithEmailAndPassword(
        secondaryAuth,
        email,
        password
      );

      if (displayName) {
        await updateProfile(userCredential.user, { displayName });
      }

      return userCredential;
    } finally {
      await deleteApp(secondaryApp);
    }
  }

  /**
   * Update user profile display name
   * @param user Firebase user object
   * @param displayName Display name to set
   */
  static async updateProfile(user: any, displayName: string) {
    return await updateProfile(user, { displayName });
  }

  /**
   * Sign out current user and clear all storage
   */
  static async signOut() {
    // Clear Firebase authentication
    await signOut(auth);
    
    // Clear all localStorage items
    localStorage.clear();
    
    // Clear all sessionStorage items
    sessionStorage.clear();
  }

  /**
   * Get current authenticated user
   */
  static getCurrentUser() {
    return auth.currentUser;
  }

  /**
   * Check if email already exists in Firebase Auth
   * @param email Email to check
   */
  static async checkEmailExistsInAuth(email: string): Promise<boolean> {
    try {
      const methods = await fetchSignInMethodsForEmail(auth, email);
      return methods.length > 0;
    } catch (error) {
      // If there's an error (like invalid email format), assume it doesn't exist
      debugError('Error checking email in Auth:', error);
      return false;
    }
  }
}
